/* eslint-disable complexity */

import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { disposeChildren, disposeHammer, disposeResizeObservers, registerResizeObserverForDisposal } from "@cervval.js/client-html5-core/src/components/disposable";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { DARK_HOUR_GLASS_DIALOG_OVER_Z_INDEX } from "@cervval.js/client-html5-core/src/services/hourglass/darkDiv.hourglass.service";
import { Display, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER, FLEX_CONTAINER_ROW, H_100, MARGIN, OVERFLOW } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, BOOTSTRAP_DEFAULT_BACKDROP_MODAL_Z_INDEX, BOOTSTRAP_DEFAULT_MODAL_Z_INDEX, KeyboardInput, appendHTMLElementContent, empty, setHTMLElementContent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ErrorCodes, wrapError } from "@cervval.js/common/src/errors/errors";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import Hammer from "hammerjs";
import { Modal } from "../../bootstrap";
import { CjsInputTextEditor } from "../../editors/inputTextEditor/inputTextEditor";
import { ButtonOptions, ButtonStyle, CjsButton } from "../button/button";
import { CjsErrorDisplay, ErrorDisplayOptions, defaultErrorDisplayOptions } from "../errorDisplay/errorDisplay";
import { i18n as i18nErrorDisplay } from "../errorDisplay/i18n/i18n";
import { I18nType, i18n } from "./i18n/i18n";

export type DialogBackdrop = "static" | boolean;

/** Presets for dialog */
export enum DialogPreConfig {

    /** Confirm/Cancel buttons */
    DEFAULT = 1,

    /** Add/Cancel buttons */
    ADD,

    /** DElete/Cancel buttons */
    DELETE,

    /** Cancel button */
    ALERT,

    /** Ok/Cancel buttons */
    OK_CANCEL,

    /** Yes/No buttons */
    YES_NO,

    /** Close button only */
    ERROR,

    /** Ok button only */
    OK,

    /** No button */
    NO_BUTTON
}

/** @see https://getbootstrap.com/docs/5.0/components/modal/#optional-sizes */
export enum DialogSize {
    DEFAULT = "default",
    SMALL = "small",
    LARGE = "large",
    EXTRA_LARGE = "extra-large"
}

/** @see https://getbootstrap.com/docs/5.0/components/modal/#fullscreen-modal */
export enum DialogFullScreen {
    ALWAYS = "always",
    BELOW_SM = "below-sm",
    BELOW_MD = "below-md",
    BELOW_LG = "below-lg",
    BELOW_XL = "below-xl",
    BELOW_XXL = "below-xxl"
}

/** Dialog position customized option (not available with bootstrap) */
export enum DialogPosition {
    DEFAULT = "default",
    TOP_LEFT = "top-left",
    TOP = "top",
    TOP_RIGHT = "top-right",
    CENTER_LEFT = "center-left",
    CENTER = "center",
    CENTER_RIGHT = "center-right",
    BOTTOM_LEFT = "bottom-left",
    BOTTOM = "bottom",
    BOTTOM_RIGHT = "bottom-right",
}

/** Store to handle drag */
interface DragStore {
    /** Last X position of draggable element */
    lastPosX: number;

    /** Last Y position of draggable element */
    lastPosY: number;

    /** Drag is processing */
    isDragging: boolean;

    /** True if a drag was performed */
    wasDragged: boolean;
}

/** Sections who can be hidden */
type HiddenSections = "header" | "body" | "footer"

/** Bootstrap CSS classes for modal size */
const MODAL_SM = "modal-sm";
const MODAL_LG = "modal-lg";
const MODAL_XL = "modal-xl";

/** Bootstrap CSS classes for modal fullscreen */
const MODAL_FULLSCREEN = "modal-fullscreen";
const MODAL_FULLSCREEN_SM_DOWN = "modal-fullscreen-sm-down";
const MODAL_FULLSCREEN_MD_DOWN = "modal-fullscreen-md-down";
const MODAL_FULLSCREEN_LG_DOWN = "modal-fullscreen-lg-down";
const MODAL_FULLSCREEN_XL_DOWN = "modal-fullscreen-xl-down";
const MODAL_FULLSCREEN_XXL_DOWN = "modal-fullscreen-xxl-down";

/** Bootstrap CSS classes for modal fullscreen */
const MODAL_SCROLLABLE = "modal-dialog-scrollable";

/** Dialog margin when use position option (in rem) */
const DIALOG_POSITION_MARGIN = 0.5;

const DRAGGABLE_CLASS = "draggable";

/** Available actions for footer button */
export type DialogFooterButtonAction = "confirm" | "cancel" | "close" | "unknown";
export interface DialogFooterButton {

    /** Button config */
    button: Partial<ButtonOptions>;

    /** Action realized by the button click */
    action: DialogFooterButtonAction;

    /** On click callback of button */
    callback?: () => void;
}

/** Callback type for onConfirmButtonClick and onCloseButtonClick */
export type DialogOnCloseOrClickCallback = Promise<{ preventClose?: boolean } | void>;

export type OverrideDialogButtonI18nMap = Map<DialogPreConfig, { primary?: string, secondary?: string }>;

/**
 * Default parameters of Dialog component
 */
export interface DialogOptions {

    /** Title in dialog header */
    title: AcceptedHtmlElementContent;

    /** Position X to append dialog to add to left css (eg: "300px", "30%", "5rem") */
    x: string | undefined;

    /** Position Y to append dialog to add to top css (eg: "300px", "30%", "5rem") */
    y: string | undefined;

    /** Position of dialog on window (TOP_LEFT, TOP, TOP_RIGHT, CENTER_LEFT, CENTER, CENTER_RIGHT, BOTTOM_LEFT, BOTTOM, BOTTOM_RIGHT) */
    position: DialogPosition;

    /** Dialog size : DialogSize.(SMALL (300px) | DEFAULT (500px) | LARGE (800px) | EXTRA_LARGE (1140px)) or specific width as css string (eg: "600px", "80%", "5rem") */
    size: DialogSize | string;

    /** Dialog full screen : DialogFullScreen.(ALWAYS | BELOW_SM | BELOW_MD | BELOW_LG | BELOW_XL | BELOW_XXL) or false */
    fullScreen: DialogFullScreen | boolean;

    /** When backdrop is set to static, the dialog will not close when clicking outside it */
    backdrop: DialogBackdrop;

    /** Close the dialog or not when escape key is pressed */
    closeOnEscapeKey: boolean;

    /** Puts the focus on the dialog when initialized */
    focus: boolean;

    /** Don't show close button in header */
    enableDismiss: boolean;

    /** Hide dialog automatically after timeout (in ms) */
    timeoutDismiss: undefined | number;

    /** Remove class .modal-dialog-scrollable : scroll will be at the right of the page instead being into dialog body (header and footer won't be sticky anymore) */
    contentScrollable: boolean;

    /** Automatically show dialog. Else call dialog.show() method to open it */
    openOnInit: boolean;

    /** Custom buttons to add to dialog footer */
    footerButtons: undefined | DialogFooterButton[];

    /** Hide sections in dialog */
    hideSections: undefined | HiddenSections[];

    /** Dialog over hourGlass */
    isOverHourGlass?: boolean;


    /**
     * Event trigger when click on default close footer default button or click icon on header
     */
    onCloseButtonClick: (dialog: CjsDialog) => DialogOnCloseOrClickCallback;

    /**
     * Event trigger when click on default confirm footer default button
     */
    onConfirmButtonClick: (dialog: CjsDialog) => DialogOnCloseOrClickCallback;

    /**
     * Event trigger when show() method is called
     */
    onDialogShow: (dialog: CjsDialog) => void;

    /**
     * Event trigger when modal is visible (after css transitions)
     */
    onAfterDialogShow: (dialog: CjsDialog) => void;

    /**
     * Event trigger when hide() method is called
     */
    onDialogHide: (dialog: CjsDialog) => void;

    /**
     * Event trigger when modal is not visible (after css transitions)
     */
    onAfterDialogHide: (dialog: CjsDialog) => void;

    /**
     * Event trigger when the modal is shown AND
     * its backdrop is static and a click outside the modal OR 
     * an escape key press is performed with the keyboard option or data-bs-keyboard set to false.
     */
    onDialogHidePrevented: (dialog: CjsDialog) => void;

    /** Configuration of dialog */
    preConfig: DialogPreConfig;

    /** Disable keyboard input behavior added by action of footer button */
    disableFooterButtonsKeyboardInput?: boolean;

    /** Enable draggable dialog */
    enableDraggable: boolean;

    /** modal-content style position */
    modalContentStylePosition: string;

    /** modal-body style position */
    modalBodyStylePosition: string;

    /**
     * Enable content visible for dialog
     * ! Temporary option, mainly used to display ShowPopup over dialog !
     */
    enableContentOverflowVisible?: boolean;

    /** Add a button where click behavior show/hide content of dialog */
    enableHeaderButtonToggleVisibility?: boolean;

    /** by default all key events are captured and not propagated outside the component */
    doNotCaptureKeyEvents?: boolean | KeyboardInput[];

    /** Override i18n label of preConfig dialogs */
    overrideDialogButtonI18n?: OverrideDialogButtonI18nMap;
}

/**
 * Default parameters of Dialog component
 */
export const defaultDialogOptions: DialogOptions = {

    /** Title in dialog header */
    title: "",

    /** Position X to append dialog to add to left css (eg: "300px", "30%", "5rem") */
    x: undefined,

    /** Position Y to append dialog to add to top css (eg: "300px", "30%", "5rem") */
    y: undefined,

    /** Position of dialog on window (TOP_LEFT, TOP, TOP_RIGHT, CENTER_LEFT, CENTER, CENTER_RIGHT, BOTTOM_LEFT, BOTTOM, BOTTOM_RIGHT) */
    position: "default" as DialogPosition,

    /** Dialog size : DialogSize.(SMALL (300px) | DEFAULT (500px) | LARGE (800px) | EXTRA_LARGE (1140px)) or specific width as css string (eg: "600px", "80%", "5rem") */
    size: "default",

    /** Dialog full screen : DialogFullScreen.(ALWAYS | BELOW_SM | BELOW_MD | BELOW_LG | BELOW_XL | BELOW_XXL) or false */
    fullScreen: false,

    /** When backdrop is set to static, the dialog will not close when clicking outside it */
    backdrop: "static",

    /** Close the dialog or not when escape key is pressed */
    closeOnEscapeKey: false,

    /** Puts the focus on the dialog when initialized */
    focus: true,

    /** Don't show close button in header */
    enableDismiss: true,

    /** Hide dialog automatically after timeout (in ms) */
    timeoutDismiss: undefined,

    /** Remove class .modal-dialog-scrollable : scroll will be at the right of the page instead being into dialog body (header and footer won't be sticky anymore) */
    contentScrollable: true,

    /** Automatically show dialog. Else call dialog.show() method to open it */
    openOnInit: true,

    /** Custom buttons to add to dialog footer */
    footerButtons: undefined,

    /** Hide sections in dialog */
    hideSections: undefined,

    /** On after dialog hide callback */
    onAfterDialogHide: () => { },

    /** On Close Button click callback */
    onCloseButtonClick: () => { return Promise.resolve(); },

    /** On after dialog show callback */
    onAfterDialogShow: () => { },

    /** On Confirm button click */
    onConfirmButtonClick: () => { return Promise.resolve(); },

    /** on dialog hide callback */
    onDialogHide: () => { },

    /** On Dialog hide prevented callback */
    onDialogHidePrevented: () => { },

    /** On dialog show callback */
    onDialogShow: () => { },

    /** Configuration of dialog */
    preConfig: DialogPreConfig.DEFAULT,

    /** 
     * Enable draggable dialog
     * @default true drag is enable by default in cjs
     */
    enableDraggable: true,

    /** modal-content style position is initial in order to allow showPopup elements to be displayed over the dialog */
    modalContentStylePosition: "initial",

    /** modal-body style position is initial in order to allow showPopup elements to be displayed over the dialog */
    modalBodyStylePosition: "initial"
};

/** Options for body */
export interface DialogBodyOptions {

    /** Center content in body */
    center?: boolean;

    /** Css classes to add to body container */
    classes?: string[];
}

/** Type of event to listen */
export const DIALOG_KEYBOARD_KEY_UP_EVENT_TYPE = "keyup";

@CustomElement({
    name: "cjs-dialog",
    template: require("./dialog.html"),
    style: require("./dialog.scss"),
    elementCssClasses: [H_100, FLEX_CONTAINER],
    i18n,
    doNotRefreshAfterInit: true
})
export class CjsDialog<I18n extends I18nType = I18nType> extends WebComponent<I18n> {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /** All dialog options */
    @TypedWebComponentOptions(defaultDialogOptions)
    public readonly options!: WebComponentOptions<DialogOptions>;

    /** HTML components */
    @TypedHtmlElements()
    public readonly components!: {
        dialogModal: HTMLDivElement,
        dialogModalDialog: HTMLDivElement,
        dialogContent: HTMLDivElement,
        dialogTitle: HTMLHeadElement,
        dialogHeader: HTMLDivElement,
        dialogHeaderCloseButton: HTMLButtonElement,
        dialogBody: HTMLDivElement,
        dialogFooter: HTMLDivElement
    };

    /** Event triggered when component has been resized */
    @Event(NoBubblesEventInit)
    public dialogResizeEvent!: EventHandler;

    /** Bootstrap modal used in dialog */
    protected _modal?: Modal;

    /** Buttons in footer of dialog */
    public footerButtons: CjsButton[] = [];

    /**
     * Confirm button in footer of dialog
     * ! undefined if footerButton option is given
     */
    public confirmButton?: CjsButton;

    /**
     * Cancel / Close button in footer of dialog
     * ! undefined if footerButton option is given
     */
    public closeButton?: CjsButton;

    /**
     * Store current visibility state of dialog content (toggled with btn in header)
     */
    public headerVisibilityToggleState = true;

    /**
     * Listener for visibility toggle of dialog content
     */
    private _headerToggleListenerObject?: EventListenerObject;

    /**
     * Button to toggle visibility of dialog content (append in header)
     */
    public headerToggleVisibilityButton = new CjsButton({
        icon: {
            name: "fa-eye-slash",
            fixedWidth: true
        },
        iconMode: "only",
        refAttribute: "headerToggleVisibilityButton",
        cssStyle: ButtonStyle.NONE,
        outline: true,
        rounded: true,
        disableBoxShadow: true,
        tooltip: {
            title: this.i18n.toggleVisibility.hide,
            delay: 500
        }
    });


    protected _elementsToDispose: {
        hammer?: HammerManager,
        resizeObservers?: ResizeObserver[]
    } = {};
    protected _isDisposed = false;

    /** Check if dialog can be resized with resize observer */
    private _canResizeDialog = true;

    constructor(options?: Partial<DialogOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);

        // Add dialog to document body
        // ! Should be called here, not in init for Dialog working properly
        this.addToDocumentBody();
    }

    /**
     * Init component
     */
    public override init(): void {
        if (this._isDisposed) {
            // dialog was disposed => don't build it
            return;
        }
        // Install draggable dialog if required
        this._installDraggable();

        // Init listeners for events
        this._initListeners();

        // Init bootstrap modal
        this._modal = new Modal(this.components.dialogModal, { backdrop: this.options.backdrop, keyboard: this.options.closeOnEscapeKey, focus: this.options.focus });

        // Show modal after instantiation
        if (this.options.openOnInit) {
            this.isInitDone = true;
            this.show();
        }
    }

    /**
     * Refresh component
     */
    public override refresh(): void {
        if (this._isDisposed) {
            this._log?.error("Element is disposed");
            return;
        }
        // Refresh dialog title
        this._refreshTitle();

        // Refresh buttons in footer
        this._refreshFooterButtons();

        // Style Position
        this.components.dialogContent.style.position = this.options.modalContentStylePosition;
        this.components.dialogBody.style.position = this.options.modalBodyStylePosition;

        // Show/hide close button icon into header
        toggleVisible(this.components.dialogHeaderCloseButton, this.options.enableDismiss, Display.BLOCK);

        // Add dialog scrollable class
        toggleClass(this.components.dialogModalDialog, this.options.contentScrollable, MODAL_SCROLLABLE);

        // Manage class for size options
        this._removeSizeOptionalClasses();

        switch (this.options.size) {
            case DialogSize.SMALL: {
                this.components.dialogModalDialog.classList.add(MODAL_SM);
                break;
            }
            case DialogSize.LARGE: {
                this.components.dialogModalDialog.classList.add(MODAL_LG);
                break;
            }
            case DialogSize.EXTRA_LARGE: {
                this.components.dialogModalDialog.classList.add(MODAL_XL);
                break;
            }
            default: {
                if (this.options.size && Object.keys(DialogSize).indexOf(this.options.size) === -1) {
                    this.components.dialogModalDialog.style.maxWidth = this.options.size;
                } else {
                    this.components.dialogModalDialog.style.maxWidth = "";
                }
                break;
            }
        }

        // Manage class for full screen options
        this._removeFullScreenOptionalClasses();

        switch (this.options.fullScreen) {
            case DialogFullScreen.ALWAYS: {
                this.components.dialogModalDialog.classList.add(MODAL_FULLSCREEN);
                break;
            }
            case DialogFullScreen.BELOW_SM: {
                this.components.dialogModalDialog.classList.add(MODAL_FULLSCREEN_SM_DOWN);
                break;
            }
            case DialogFullScreen.BELOW_MD: {
                this.components.dialogModalDialog.classList.add(MODAL_FULLSCREEN_MD_DOWN);
                break;
            }
            case DialogFullScreen.BELOW_LG: {
                this.components.dialogModalDialog.classList.add(MODAL_FULLSCREEN_LG_DOWN);
                break;
            }
            case DialogFullScreen.BELOW_XL: {
                this.components.dialogModalDialog.classList.add(MODAL_FULLSCREEN_XL_DOWN);
                break;
            }
            case DialogFullScreen.BELOW_XXL: {
                this.components.dialogModalDialog.classList.add(MODAL_FULLSCREEN_XXL_DOWN);
                break;
            }
            default: {
                break;
            }
        }

        // Hide dialog sections
        if (this.options.hideSections != null) {
            toggleVisible(this.components.dialogHeader, !this.options.hideSections.includes("header"), Display.FLEX);
            toggleVisible(this.components.dialogBody, !this.options.hideSections.includes("body"), Display.FLEX);
            toggleVisible(this.components.dialogFooter, !this.options.hideSections.includes("footer"), Display.FLEX);
        }

        // Set dialog position on window
        this._refreshPosition();

        // Refresh visible content 
        this._refreshContentVisible();

        // Hide dialog automatically after timeout
        if (this.options.timeoutDismiss) {
            setTimeout(() => {
                this.hide();
            }, this.options.timeoutDismiss);
        }

        // Refresh header click toggle
        this._refreshEnableHeaderClickHideContent();

        // Refresh the option IsOverHourGlass
        this._refreshIsOverHourGlass();

        // Refresh dialog zIndex if dialog is opened over a previous dialog
        this.refreshDialogInDialog();
    }

    /** Called by browser when element is removed from DOM */
    public disconnectedCallback(): void {
        this.removeEventListener(DIALOG_KEYBOARD_KEY_UP_EVENT_TYPE, this._onKeyboardKeyPress);
    }

    /**
     * Init listeners
     */
    protected _initListeners(): void {
        // Hide prevented (when click outside outside dialog and backdrop is static or press escape key and close on escape key is false)
        this.components.dialogModal.addEventListener("hidePrevented.bs.modal", () => {
            this.options.onDialogHidePrevented?.(this);
        });

        // When dialog is showing
        this.components.dialogModal.addEventListener("show.bs.modal", () => {
            this.options.onDialogShow?.(this);
        });

        // When dialog is shown
        this.components.dialogModal.addEventListener("shown.bs.modal", () => {
            this.options.onAfterDialogShow?.(this);
        });

        // When dialog is hiding
        this.components.dialogModal.addEventListener("hide.bs.modal", () => {
            this.options.onDialogHide?.(this);
        });

        // When dialog is hidden
        this.components.dialogModal.addEventListener("hidden.bs.modal", () => {
            this.options.onAfterDialogHide?.(this);
            // Remove instantiation when modal is hidden
            this.remove();
        });

        // Click on close header button
        this.components.dialogHeaderCloseButton.onclick = () => {
            this.options.onCloseButtonClick?.(this).then(args => this.handlePreventClose(args));
        };

        // Add resize observe
        const resizeObserver = new ResizeObserver(() => {
            // Check if dialog should update his position on this resize event
            if (!this._canResizeDialog) { this._canResizeDialog = true; return; }

            this._refreshPosition();
        });
        resizeObserver.observe(this.components.dialogModal);
        resizeObserver.observe(this.components.dialogContent);
        registerResizeObserverForDisposal(this._elementsToDispose, resizeObserver);

        const captureEvent = (e: KeyboardEvent): void => {
            // verify if the event is on the child
            const target = e.target as HTMLElement;

            if (this.options.doNotCaptureKeyEvents === true || this.contains(target) ||
                Array.isArray(this.options.doNotCaptureKeyEvents) && this.options.doNotCaptureKeyEvents.includes(e.key as unknown as KeyboardInput)) {
                return;
            }
            // capture event to avoid unexpected event trigger outside in the application
            e.stopPropagation();
        };
        this.addEventListener("keydown", captureEvent);
        this.addEventListener("keypress", captureEvent);
        this.addEventListener("keyup", captureEvent);
    }

    /** Callback when keyboard event is trigger */
    private _onKeyboardKeyPress = (e: KeyboardEvent): void => {
        if (!this.options.disableFooterButtonsKeyboardInput && this.options.footerButtons) {
            for (const buttonConfig of this.options.footerButtons) {
                // Confirm with ENTER keypress
                if (e.key === KeyboardInput.ENTER && document.activeElement === this.components.dialogModal && buttonConfig.action === "confirm") {
                    buttonConfig.callback?.();
                }
                // Cancel with ESC keypress, allow only if backdrop isn't static
                if (this.options.backdrop !== "static" && e.key === KeyboardInput.ESCAPE && (buttonConfig.action === "cancel" || buttonConfig.action === "close")) {
                    buttonConfig.callback?.();
                }
            }
        }
    };

    /**
     * Handle if we need to close dialog or not
     */
    private handlePreventClose(args: void | { preventClose?: boolean }): void {
        if (!args?.preventClose) { this.hide(); }
    }

    /**
     * Refresh dialog position
     */
    protected _refreshPosition(): void {

        // Don't refresh position if dialog is currently in fullscreen
        if (this._isFullscreenDialog()) {
            this.components.dialogModal.style.left = toPx(0);
            this.components.dialogModal.style.top = toPx(0);
            return;
        }

        // Don't refresh position if user already move dialog
        if (this._dragStore.wasDragged) {
            return;
        }

        // Update margin if needed
        if (this.options.position && this.options.position !== DialogPosition.DEFAULT) {
            this.components.dialogModalDialog.style.margin = DIALOG_POSITION_MARGIN + "rem";
        }

        switch (this.options.position) {
            case DialogPosition.TOP_LEFT:
                this.components.dialogModal.style.top = `${DIALOG_POSITION_MARGIN}rem`;
                this.components.dialogModal.style.left = `${DIALOG_POSITION_MARGIN * 2}rem`;
                break;
            case DialogPosition.TOP:
                this.components.dialogModal.style.top = `${DIALOG_POSITION_MARGIN}rem`;
                this.components.dialogModal.style.left = `calc(${window.innerWidth / 2 - this.components.dialogContent.offsetWidth / 2}px - ${DIALOG_POSITION_MARGIN}rem)`;
                break;

            case DialogPosition.TOP_RIGHT:
                this.components.dialogModal.style.top = `${DIALOG_POSITION_MARGIN}rem`;
                this.components.dialogModal.style.left = `calc(${window.innerWidth - this.components.dialogContent.offsetWidth}px - ${DIALOG_POSITION_MARGIN * 2}rem)`;
                break;

            case DialogPosition.CENTER_LEFT:
                this.components.dialogModal.style.top = `calc(${window.innerHeight / 2 - this.components.dialogContent.clientHeight / 2}px - ${DIALOG_POSITION_MARGIN}rem)`;
                this.components.dialogModal.style.left = `${DIALOG_POSITION_MARGIN * 2}rem`;
                break;


            case DialogPosition.CENTER_RIGHT:
                this.components.dialogModal.style.top = `calc(${window.innerHeight / 2 - this.components.dialogContent.clientHeight / 2}px - ${DIALOG_POSITION_MARGIN}rem)`;
                this.components.dialogModal.style.left = `calc(${window.innerWidth - this.components.dialogContent.offsetWidth}px - ${DIALOG_POSITION_MARGIN * 2}rem)`;
                break;

            case DialogPosition.BOTTOM_LEFT:
                this.components.dialogModal.style.top = `calc(${window.innerHeight - this.components.dialogContent.clientHeight}px - ${DIALOG_POSITION_MARGIN * 2}rem)`;
                this.components.dialogModal.style.left = `${DIALOG_POSITION_MARGIN * 2}rem`;
                break;

            case DialogPosition.BOTTOM:
                this.components.dialogModal.style.top = `calc(${window.innerHeight - this.components.dialogContent.clientHeight}px - ${DIALOG_POSITION_MARGIN * 2}rem)`;
                this.components.dialogModal.style.left = `calc(${window.innerWidth / 2 - this.components.dialogContent.offsetWidth / 2}px`;
                break;

            case DialogPosition.BOTTOM_RIGHT:
                this.components.dialogModal.style.top = `calc(${window.innerHeight - this.components.dialogContent.clientHeight}px - ${DIALOG_POSITION_MARGIN * 2}rem)`;
                this.components.dialogModal.style.left = `calc(${window.innerWidth - this.components.dialogContent.offsetWidth}px - ${DIALOG_POSITION_MARGIN * 2}rem)`;
                break;
            case DialogPosition.CENTER:
            default: {
                let top = `calc(${window.innerHeight / 2 - this.components.dialogContent.clientHeight / 2}px - ${DIALOG_POSITION_MARGIN}rem)`;
                if (!this.options.contentScrollable) {// ! Disable top calc if content isn't scrollable
                    top = "0px";
                }
                this.components.dialogModal.style.top = top;
                this.components.dialogModal.style.left = `calc(${window.innerWidth / 2 - this.components.dialogContent.offsetWidth / 2}px)`;
                break;
            }
        }

        this.components.dialogModalDialog.classList.add(MARGIN.M_0);

        // Set x and y position
        if (this.options.x) {
            this.components.dialogModal.style.left = this.options.x;
        }
        if (this.options.y) {
            this.components.dialogModal.style.top = this.options.y;
        }

    }

    /** @returns true if dialog is currently in fullscreen mode */
    private _isFullscreenDialog(): boolean {
        // Don't refresh position on fullscreen
        if (!this.options.fullScreen) { return false; }

        const width = window.innerWidth;
        switch (this.options.fullScreen) {
            case DialogFullScreen.BELOW_SM:
                return width < 576;
            case DialogFullScreen.BELOW_MD:
                return width < 768;
            case DialogFullScreen.BELOW_LG:
                return width < 992;
            case DialogFullScreen.BELOW_XL:
                return width < 1200;
            case DialogFullScreen.BELOW_XXL:
                return width < 1400;
            default:
                return true;
        }
    }

    /** Refresh content classes to toggle visible/hidden behavior */
    protected _refreshContentVisible(): void {
        // The overflow visible is set to avoid show popup to be hidden by the dialog footer
        toggleClass(this.components.dialogBody, this.options.enableContentOverflowVisible, OVERFLOW.VISIBLE);
        toggleClass(this.components.dialogContent, this.options.enableContentOverflowVisible, OVERFLOW.VISIBLE);

        // Don't use overflow visible if the dialog is there is enough space in the dialog body to open a show popup normally
        if (this.options.enableContentOverflowVisible) {
            const dialogContentBoundingRect = this.components.dialogContent.getBoundingClientRect();
            const arbitraryCoefficient = 0.9;
            // Subtract the window's height and multiply an arbitrary coefficient to keep a marge
            const toggleOverflowVisible = dialogContentBoundingRect.height < window.innerHeight * arbitraryCoefficient;
            toggleClass(this.components.dialogBody, toggleOverflowVisible, OVERFLOW.VISIBLE);
            toggleClass(this.components.dialogContent, toggleOverflowVisible, OVERFLOW.VISIBLE);
        }
    }

    /**
     * ensures modal is defined and shows an error in console if not
     */
    protected _ensureModalIsSet(modal: Modal | undefined): modal is Modal {
        if (modal == null) {
            if (this._isDisposed) {
                this._log?.error("dialog was disposed");
            } else if (!this.isInitDone) {
                this._log?.error("function was called before init");
            } else {
                this._log?.error("internal error modal is not defined");
            }
            return false;
        }
        return true;
    }

    /**
     * Show dialog box
     */
    public show(): void {
        if (!this._ensureModalIsSet(this._modal)) { return; }
        this._modal.show();
        this.refresh();
    }

    /**
     * Hide dialog box
     */
    public hide(): void {
        // this._isDisposed: if disposed we don't display an error message for hide
        if (this._isDisposed || !this._ensureModalIsSet(this._modal)) { return; }
        this._modal.hide();
    }

    /**
     * Readjust the modal’s position if the height of a modal changes while it is open (i.e. in case a scrollbar appears)
     */
    public handleUpdate(): void {
        if (!this._ensureModalIsSet(this._modal)) { return; }
        this._modal.handleUpdate();
    }

    /**
     * Destroys an element’s modal. (Removes stored data on the DOM element and dispose all children)
     */
    public dispose(p?: { doNotDisposeChildren?: boolean }): void {
        if (this._isDisposed) {
            return;
        }
        this._isDisposed = true;
        if (this._modal) {
            this._modal.dispose();
            this._modal = undefined;
        }
        disposeHammer(this._elementsToDispose);
        disposeResizeObservers(this._elementsToDispose);
        if (!p?.doNotDisposeChildren) { disposeChildren(this); }
        this.lightDomRoot = undefined;
    }

    /**
     * Add buttons to dialog footer
     * Button footers can be customized in constructor options using DialogFooterButton configs to build them
     */
    protected _refreshFooterButtons(): void {
        this.footerButtons = [];// Reset
        empty(this.components.dialogFooter);

        if (this.options.preConfig) {
            this.setPreConfigButtons(this.options.preConfig);
        }

        if (this.options.footerButtons) {
            // Add buttons to dialog footer
            for (const buttonConfig of this.options.footerButtons) {
                const btn = new CjsButton(buttonConfig.button);

                if (buttonConfig.callback) {
                    btn.clickEvent.on(buttonConfig.callback);
                }

                if (buttonConfig.action === "confirm") {
                    this.confirmButton = btn;
                } else if (buttonConfig.action === "cancel" || buttonConfig.action === "close") {
                    this.closeButton = btn;
                }

                this.footerButtons.push(btn);
                this.addToFooter(btn);
            }

            this.addEventListener(DIALOG_KEYBOARD_KEY_UP_EVENT_TYPE, this._onKeyboardKeyPress);
        }

    }


    /**
     * Get default buttons to add to footer
     */
    public getDefaultFooterButtons(): DialogFooterButton[] {

        const confirmButton: DialogFooterButton = {
            button: {
                label: this.i18n.confirmButton.label,
                cssStyle: ButtonStyle.PRIMARY,
                refAttribute: "confirmDialogButton"
            },
            action: "confirm",
            callback: () => {
                this.options.onConfirmButtonClick?.(this).then(args => this.handlePreventClose(args)).catch(showErrorInDialog);
            }
        };

        const closeButton: DialogFooterButton = {
            button: {
                label: this.i18n.closeButton.label,
                cssStyle: ButtonStyle.SECONDARY,
                refAttribute: "closeDialogButton"
            },
            action: "close",
            callback: () => {
                this.options.onCloseButtonClick?.(this).then(args => this.handlePreventClose(args)).catch((err) => showErrorDialog({ error: err }));
            }
        };

        return [
            confirmButton,
            closeButton
        ];

    }

    /**
     * Add dialog element to document body
     */
    public addToDocumentBody(): void {
        document.body.append(this);
    }

    /**
     * Set dialog title
     */
    protected _refreshTitle(): void {
        setHTMLElementContent(this.components.dialogTitle, this.options.title);
    }

    /**
     * Set content to dialog body
     *
     * @param content Content to set to body
     */
    public setBody(content: AcceptedHtmlElementContent, options?: DialogBodyOptions): void {
        this.components.dialogBody.innerHTML = "";
        this.addToBody(content, options);
    }

    /**
     * Add content to dialog body
     *
     * @param content Content to add to body
     */
    public addToBody(content: AcceptedHtmlElementContent, options?: DialogBodyOptions): void {
        appendHTMLElementContent(this.components.dialogBody, content);

        if (options?.center) {
            this.components.dialogBody.classList.add(FLEX_CONTAINER_ROW, "justify-center");
        }
        if (options?.classes) {
            this.components.dialogBody.classList.add(...options.classes);
        }

        // Refresh visible content 
        this._refreshContentVisible();
    }

    /**
     * Add content to footer
     *
     * @param content Content to add to footer
     */
    public addToFooter(content: AcceptedHtmlElementContent): void {
        appendHTMLElementContent(this.components.dialogFooter, content);
    }

    /**
     * Remove all size optional classes from dialog
     */
    protected _removeSizeOptionalClasses(): void {
        this.components.dialogModalDialog.classList.remove(
            MODAL_SM,
            MODAL_LG,
            MODAL_XL);
    }

    /**
     * Remove all full screen optional classes from dialog
     */
    protected _removeFullScreenOptionalClasses(): void {
        this.components.dialogModalDialog.classList.remove(
            MODAL_FULLSCREEN,
            MODAL_FULLSCREEN_SM_DOWN,
            MODAL_FULLSCREEN_MD_DOWN,
            MODAL_FULLSCREEN_LG_DOWN,
            MODAL_FULLSCREEN_XL_DOWN,
            MODAL_FULLSCREEN_XXL_DOWN);
    }

    /** Refresh header click */
    private _refreshEnableHeaderClickHideContent(): void {

        // If listener is already added but option has changed, remove listener
        if (this._headerToggleListenerObject && !this.options.enableHeaderButtonToggleVisibility) {
            this.headerToggleVisibilityButton.clickEvent.off(this._headerToggleListenerObject);
            this.headerToggleVisibilityButton.remove();
            return;
        }

        // If option is disabled or listener object already existing, return
        if (!this.options.enableHeaderButtonToggleVisibility || this._headerToggleListenerObject) { return; }

        // Else, we add listener

        // Create the toggle listener
        this._headerToggleListenerObject = {
            handleEvent: () => {
                this.headerVisibilityToggleState = !this.headerVisibilityToggleState;

                // Store previous x,y position of dialog
                const { x, y } = { x: this.components.dialogModal.offsetLeft, y: this.components.dialogModal.offsetTop };
                this._canResizeDialog = false;

                // Toggle content-hidden class
                toggleClass(this, !this.headerVisibilityToggleState, "content-hidden");

                // Update button
                this.headerToggleVisibilityButton.options.setAndRefreshIfChanged({
                    icon: {
                        name: this.headerVisibilityToggleState ? "fa-eye-slash" : "fa-eye"
                    },
                    tooltip: {
                        ...this.headerToggleVisibilityButton.options,
                        title: this.headerVisibilityToggleState ? this.i18n.toggleVisibility.hide : this.i18n.toggleVisibility.show
                    }
                });

                // Update dialogModal position (keep the previous position)
                this.components.dialogModal.style.left = toPx(x);
                this.components.dialogModal.style.top = toPx(y);
            }
        };
        this.headerToggleVisibilityButton.clickEvent.on(this._headerToggleListenerObject);
        this.components.dialogTitle.after(this.headerToggleVisibilityButton);
    }

    /**
     * Update zIndex to be over the HourGlass if needed
     */
    private _refreshIsOverHourGlass(): void {
        const dialogModalStyle = this.components.dialogModal.style;
        if (this.options.isOverHourGlass) {
            dialogModalStyle.zIndex = String(DARK_HOUR_GLASS_DIALOG_OVER_Z_INDEX);
        } else if (dialogModalStyle.zIndex === String(DARK_HOUR_GLASS_DIALOG_OVER_Z_INDEX)) {// If current zIndex value is DARK_HOUR_GLASS_DIALOG_OVER_Z_INDEX previously set, remove it
            dialogModalStyle.zIndex = "";// Delete attribute style value
        }
    }

    /**
     * Install a predefined dialog config
     * ! Will override previous footer buttons already set !
     * 
     * @param preConfig of Dialog to setup
     */
    public setPreConfigButtons(preConfig: DialogPreConfig): void {

        const confirmCb = (): void => {
            this.options.onConfirmButtonClick?.(this).then(args => this.handlePreventClose(args)).catch(showErrorInDialog);
        };
        const closeCb = (): void => {
            this.options.onCloseButtonClick?.(this).then(args => this.handlePreventClose(args)).catch(showErrorInDialog);
        };

        const i18nOverride = this.options.overrideDialogButtonI18n?.get(preConfig);

        switch (preConfig) {
            case DialogPreConfig.ADD:


                this.components.dialogHeader.classList.add("bg-primary", "text-white");
                this.components.dialogHeaderCloseButton.classList.add("btn-close-white");
                this.headerToggleVisibilityButton.components.button.classList.add("btn-close-white");
                if (this.options.footerButtons) { break; }// Don't refresh button if config is given

                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: {// button will be accessible via CjsDialog.confirmButton if you need to customize it
                                cssStyle: ButtonStyle.PRIMARY,
                                label: i18nOverride?.primary ?? this.i18n.addButton.label,
                                refAttribute: "addButton"
                            },
                            action: "confirm",
                            callback: confirmCb
                        },
                        {
                            button: {// button will be accessible via CjsDialog.closeButton if you need to customize it
                                cssStyle: ButtonStyle.SECONDARY,
                                label: i18nOverride?.secondary ?? this.i18n.cancelButton.label,
                                refAttribute: "cancelButton"
                            },
                            action: "cancel",
                            callback: closeCb
                        }
                    ]
                });

                break;
            case DialogPreConfig.DELETE:

                this.components.dialogHeader.classList.add("bg-danger", "text-white");
                this.components.dialogHeaderCloseButton.classList.add("btn-close-white");
                this.headerToggleVisibilityButton.components.button.classList.add("btn-close-white");

                if (this.options.footerButtons) { break; }// Don't refresh button if config is given

                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: {// button will be accessible via CjsDialog.confirmButton if you need to customize it
                                cssStyle: ButtonStyle.DANGER,
                                label: i18nOverride?.primary ?? this.i18n.deleteButton.label,
                                refAttribute: "deleteButton"
                            },
                            action: "confirm",
                            callback: confirmCb
                        },
                        {
                            button: {// button will be accessible via CjsDialog.closeButton if you need to customize it
                                cssStyle: ButtonStyle.SECONDARY,
                                label: i18nOverride?.secondary ?? this.i18n.cancelButton.label,
                                refAttribute: "cancelButton"
                            },
                            action: "cancel",
                            callback: closeCb
                        }
                    ]
                });

                break;
            case DialogPreConfig.ALERT:

                if (this.options.footerButtons) { break; }// Don't refresh button if config is given

                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: {// button will be accessible via CjsDialog.closeButton if you need to customize it
                                cssStyle: ButtonStyle.SECONDARY,
                                label: i18nOverride?.secondary ?? this.i18n.closeButton.label,
                                refAttribute: "closeButton"
                            },
                            action: "close",
                            callback: closeCb
                        }
                    ],
                    enableDismiss: false
                });

                break;
            case DialogPreConfig.OK_CANCEL:

                if (this.options.footerButtons) { break; }// Don't refresh button if config is given
                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: {// button will be accessible via CjsDialog.confirmButton if you need to customize it
                                cssStyle: ButtonStyle.PRIMARY,
                                label: i18nOverride?.primary ?? this.i18n.okButton.label,
                                refAttribute: "okButton"
                            },
                            action: "confirm",
                            callback: confirmCb
                        },
                        {
                            button: {// button will be accessible via CjsDialog.closeButton if you need to customize it
                                cssStyle: ButtonStyle.SECONDARY,
                                label: i18nOverride?.secondary ?? this.i18n.cancelButton.label,
                                refAttribute: "cancelButton"
                            },
                            action: "cancel",
                            callback: closeCb
                        }
                    ]
                });
                break;
            case DialogPreConfig.OK:

                if (this.options.footerButtons) { break; }// Don't refresh button if config is given
                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: { // button will be accessible via CjsDialog.confirmButton if you need to customize it
                                cssStyle: ButtonStyle.PRIMARY,
                                label: i18nOverride?.primary ?? this.i18n.okButton.label,
                                refAttribute: "okButton"
                            },
                            action: "confirm",
                            callback: confirmCb
                        }
                    ]
                });
                break;
            case DialogPreConfig.YES_NO:

                if (this.options.footerButtons) { break; }// Don't refresh button if config is given
                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: {// button will be accessible via CjsDialog.confirmButton if you need to customize it
                                cssStyle: ButtonStyle.PRIMARY,
                                label: i18nOverride?.primary ?? this.i18n.yesButton.label,
                                refAttribute: "yesButton"
                            },
                            action: "confirm",
                            callback: confirmCb
                        },
                        {
                            button: {// button will be accessible via CjsDialog.closeButton if you need to customize it
                                cssStyle: ButtonStyle.SECONDARY,
                                label: i18nOverride?.secondary ?? this.i18n.noButton.label,
                                refAttribute: "noButton"
                            },
                            action: "cancel",
                            callback: closeCb
                        }
                    ]
                });
                break;
            case DialogPreConfig.ERROR:

                // If title isn't defined, set default title of errorDialogTitle
                if (!this.options.title) {
                    this.options.setWithoutRefresh({ title: i18nErrorDisplay().errorDialogTitle.label });
                    this._refreshTitle();
                }

                if (this.options.footerButtons) { break; }// Don't refresh button if config is given
                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: {// button will be accessible via CjsDialog.closeButton if you need to customize it
                                cssStyle: ButtonStyle.SECONDARY,
                                label: i18nOverride?.secondary ?? this.i18n.closeButton.label,
                                refAttribute: "closeButton"
                            },
                            action: "close",
                            callback: closeCb
                        }
                    ], backdrop: true,
                    size: this.options.size ?? DialogSize.LARGE,
                    hideSections: ["footer"]
                });

                break;
            case DialogPreConfig.NO_BUTTON:
                // nothing to do
                break;
            case DialogPreConfig.DEFAULT:
            default:
                if (this.options.footerButtons) { break; }// Don't refresh button if config is given

                this.options.setWithoutRefresh({
                    footerButtons: [
                        {
                            button: {// button will be accessible via CjsDialog.confirmButton if you need to customize it
                                label: i18nOverride?.primary ?? this.i18n.confirmButton.label,
                                cssStyle: ButtonStyle.PRIMARY,
                                refAttribute: "confirmDialogButton"
                            },
                            action: "confirm",
                            callback: confirmCb
                        },
                        {
                            button: {// button will be accessible via CjsDialog.closeButton if you need to customize it
                                label: i18nOverride?.secondary ?? this.i18n.closeButton.label,
                                cssStyle: ButtonStyle.SECONDARY,
                                refAttribute: "closeDialogButton"
                            },
                            action: "close",
                            callback: closeCb
                        }
                    ]
                });
        }
    }

    /** 
     * Append errorDisplay component to body of dialog
     * ? Use with option.preConfig = DialogPreConfig.ERROR is optional but advised
     */
    public setErrorDisplay(errorOptions: Partial<ErrorDisplayOptions>): void {

        const errorDisplay = new CjsErrorDisplay();

        // Merge default options of errorDisplay with parameter ones
        errorDisplay.options.setAndRefreshIfChanged({
            ...defaultErrorDisplayOptions,
            ...errorOptions
        });
        this.addToBody(errorDisplay);
    }

    /**
     * ####################################
     * ######## DRAGGABLE SECTION #########
     * ####################################
     */

    /** Used to handle drag */
    private _dragStore: DragStore = {
        lastPosX: 0,
        lastPosY: 0,
        isDragging: false,
        wasDragged: false
    };

    /** Install draggable behavior for dialog */
    private _installDraggable(): void {
        if (!this.options.enableDraggable) { return; }

        this.classList.add(DRAGGABLE_CLASS);

        // Handle draggable event
        const mc = new Hammer(this.components.dialogHeader);
        mc.add(new Hammer.Pan({ direction: Hammer.DIRECTION_ALL, threshold: 0 }));
        mc.on("pan", (evt) => this._handleDrag(evt));
        this._elementsToDispose.hammer = mc;

        // Add resize observe
        const dragResizeObserver = new ResizeObserver(() => {
            this._refreshPosition();
        });
        dragResizeObserver.observe(document.body);
        registerResizeObserverForDisposal(this._elementsToDispose, dragResizeObserver);
    }

    /**
     * Handle draggable event 
     * @param ev Event
     */
    private _handleDrag(ev: HammerInput): void {

        // Get reference of draggable element
        const draggableElement = this.components.dialogModal;
        const draggableContent = this.components.dialogContent;

        // Store the current position and start dragging
        if (!this._dragStore.isDragging) {
            this._dragStore.isDragging = true;
            this._dragStore.wasDragged = true;
            this._dragStore.lastPosX = draggableElement.offsetLeft;
            this._dragStore.lastPosY = draggableElement.offsetTop;
        }

        // Calculate next positions
        const nextLeft = ev.deltaX + this._dragStore.lastPosX;
        const nextTop = ev.deltaY + this._dragStore.lastPosY;
        const nextRight = nextLeft + draggableContent.getBoundingClientRect().width;
        const nextBottom = nextTop + draggableContent.getBoundingClientRect().height;

        // Check conditions before moving element
        const isInsideLeftWindow = nextLeft > 0;
        const isInsideTopWindow = nextTop > 0;
        const isInsideRightWindow = nextRight < window.innerWidth;
        const isInsideBottomWindow = nextBottom < window.innerHeight;

        // Move element if element can be display in window: Left & Right bounds
        if (isInsideLeftWindow && isInsideRightWindow) {
            draggableElement.style.left = toPx(nextLeft);
        } else if (isInsideLeftWindow && !isInsideRightWindow) { // Right bound
            draggableElement.style.left = window.innerWidth - draggableContent.getBoundingClientRect().width + "px";
        } else if (!isInsideLeftWindow && isInsideRightWindow) {// Left bound
            draggableElement.style.left = "1px"; // Set to 1px for display, but in theorical, value is 0px
        }

        // Move element if element can be display in window: Top & Bottom bounds
        if (isInsideTopWindow && isInsideBottomWindow) {
            draggableElement.style.top = toPx(nextTop);
        } else if (isInsideTopWindow && !isInsideBottomWindow) {// Bottom bound
            draggableElement.style.top = window.innerHeight - draggableContent.getBoundingClientRect().height - 1 + "px";// Minus -1 for display, but in theorical, isn't necessary
        } else if (!isInsideTopWindow && isInsideBottomWindow) {// Top bound
            draggableElement.style.top = "1px"; // Set to 1px for display, but in theorical, value is 0px
        }


        // Stop dragging when event is finished
        if (ev.isFinal) {
            this._dragStore.isDragging = false;
        }
    }

    /**
     * Refresh zIndex for dialog in dialog, use previous dialog opened
     */
    public refreshDialogInDialog(): void {
        const siblings = this.parentElement?.children;
        if (!siblings) { return; }// Component isn't added to DOM

        // Add backdrop because dialog on dialog
        const offsetZIndex = 5;
        const zIndexForOverHourGlassAndOffset = (this.options.isOverHourGlass ? DARK_HOUR_GLASS_DIALOG_OVER_Z_INDEX : 0) + offsetZIndex;

        // Search for previous dialog opened, reverse siblings to find last dialog open in
        const previousDialog = Array.from(siblings)?.reverse().find(elt => elt instanceof CjsDialog && elt !== this) as CjsDialog | undefined;

        // If a dialog is already open, increment zIndex of this dialog
        if (previousDialog) {
            const previousDialogZIndex = Number(getComputedStyle(previousDialog.components.dialogModal).zIndex);

            // Add offset over previous dialog
            const dialogZIndexForOverHourGlassAndOffset = previousDialogZIndex + zIndexForOverHourGlassAndOffset + offsetZIndex;

            this.components.dialogModal.style.zIndex = String(dialogZIndexForOverHourGlassAndOffset);
            // Get last backdrop opened in body, update his zIndex
            const backdropsOpened = Array.from(document.querySelectorAll(".modal-backdrop.show")) as HTMLDivElement[];
            const backdrop = backdropsOpened.slice(-1)[0];

            if (backdrop) {
                backdrop.style.zIndex = String(previousDialogZIndex + zIndexForOverHourGlassAndOffset);
            }
        }
    }

    /**
     * Handle zIndex for dialog in dialog 
     * @deprecated handleDialogInDialog is performed in dialog refresh, you should not call it anymore: @see refreshDialogInDialog
     */
    public handleDialogInDialog(): void {
        // Add backdrop because dialog on dialog
        const offsetZIndex = 5;

        const zIndexForOverHourGlassAndOffset = (this.options.isOverHourGlass ? DARK_HOUR_GLASS_DIALOG_OVER_Z_INDEX : 0) + offsetZIndex;

        this.components.dialogModal.style.zIndex = String(zIndexForOverHourGlassAndOffset + BOOTSTRAP_DEFAULT_MODAL_Z_INDEX);
        // Get last backdrop opened in body, update his zIndex
        const backdropsOpened = Array.from(document.querySelectorAll(".modal-backdrop.show")) as HTMLDivElement[];
        const backdrop = backdropsOpened.slice(-1)[0];

        if (backdrop) {
            backdrop.style.zIndex = String(zIndexForOverHourGlassAndOffset + BOOTSTRAP_DEFAULT_BACKDROP_MODAL_Z_INDEX);
        }
    }
}

/**
 * Show a Dialog preconfigured for ERRORs and containing an ErrorDisplay that displays error according to errorOptions 
 */
export function showErrorDialog(errorOptions: Partial<ErrorDisplayOptions>, dialogOptions?: Partial<DialogOptions>): void {
    const dialog = new CjsDialog({
        preConfig: DialogPreConfig.ERROR,
        size: DialogSize.LARGE,
        isOverHourGlass: true,
        ...dialogOptions
    });// no need to append the dialog to the DOM, it adds itself automatically

    dialog.setErrorDisplay(errorOptions);
}

/**
 * Can be used in promise catch to display error in console and in a dialog for user
 * usage .catch(showErrorInDialog)
 * ! does not display the error dialog if err.noDialog or if aborted
 */
export function showErrorInDialog(err: unknown, dialogOptions?: Partial<DialogOptions> & Partial<ErrorDisplayOptions>): void {
    /** Application has decided to abort the request, no error display is needed */
    if (err === ErrorCodes.ABORT_ERROR_CODE) { return; }

    const error = wrapError(err);
    if (!error.doNotLog) {
        getLogConsole().error(error);
    }
    if (error.noDialog) {
        return;
    }

    const dialog = new CjsDialog({
        preConfig: DialogPreConfig.ERROR,
        size: DialogSize.LARGE,
        isOverHourGlass: true,
        ...dialogOptions
    });// no need to append the dialog to the DOM, it adds itself automatically
    dialog.setErrorDisplay({
        error,
        ...dialogOptions
    });
}

/**
 * Generates a function, can be used in promise catch to display error and call a function after
 * usage .catch(genShowErrorInDialog(refresh))
 */
export function genShowErrorInDialog(callAfterError: () => void): (err: unknown) => void {
    return (err: unknown) => {
        showErrorInDialog(err);
        callAfterError();
    };
}

/**
 * Shows a delete confirm Dialog and returns a promise with a boolean indicating if user hit Ok
 */
export function showConfirmDeleteDialog(message: { body?: AcceptedHtmlElementContent, title?: AcceptedHtmlElementContent }, options?: { dialog?: Partial<DialogOptions>, body?: DialogBodyOptions, afterDialogCreate?: (dialog: CjsDialog) => void }): Promise<boolean> {
    options ??= {};
    options.dialog ??= {};
    options.dialog.preConfig = DialogPreConfig.DELETE;
    return showConfirmDialog(message, options);
}

/**
 * Shows a Yes No Dialog and returns a promise with a boolean indicating if user hit Ok
 */
export function showConfirmDialog(message: { body?: AcceptedHtmlElementContent, title?: AcceptedHtmlElementContent }, options?: { dialog?: Partial<DialogOptions>, body?: DialogBodyOptions, afterDialogCreate?: (dialog: CjsDialog) => void }): Promise<boolean> {
    return new Promise((resolve, reject) => {
        const dialog = new CjsDialog({
            preConfig: DialogPreConfig.YES_NO,
            title: message.title,
            ...options?.dialog,
            onConfirmButtonClick: async (dlg): DialogOnCloseOrClickCallback => {
                const result = await options?.dialog?.onConfirmButtonClick?.(dlg);
                if (result?.preventClose !== true) {
                    resolve(true);
                }
                return result;
            },
            onDialogHide: (dlg) => {
                options?.dialog?.onDialogHide?.(dlg);
                // promise may have already been resolved in onConfirmButtonClick, it is ok to resolve again it will have no effect
                resolve(false);
            }
        });// no need to append the dialog to the DOM, it adds itself automatically
        if (message.body) {
            dialog.setBody(message.body, options?.body);
        }
        options?.afterDialogCreate?.(dialog);
    });
}

/**
 * Shows a Ok/Cancel Dialog with text input inside and returns a promise with the value of the input
 * You can prevent dialog close if input value is not correct with onBeforeSubmit(input,dialog) returning { preventClose : true }
 */
export function showTextInputDialog(p?: {
    title?: AcceptedHtmlElementContent,
    initialValue?: string,
    onBeforeSubmit?: (textInput: CjsInputTextEditor, dialog: CjsDialog) => Promise<{ preventClose: boolean }>
}, options?: { dialog?: Partial<DialogOptions>, body?: DialogBodyOptions }): Promise<string | undefined> {

    const textInput = new CjsInputTextEditor();
    if (p?.initialValue != null) {
        // set initial value
        textInput.setValue(p.initialValue);
    }
    return new Promise((resolve, reject) => {
        const dialog = new CjsDialog({
            preConfig: DialogPreConfig.OK_CANCEL,
            title: p?.title,
            closeOnEscapeKey: false,
            enableDismiss: false,
            ...options?.dialog,
            onConfirmButtonClick: async (dlg): DialogOnCloseOrClickCallback => {
                let result = await options?.dialog?.onConfirmButtonClick?.(dlg);
                if (result?.preventClose !== true) {
                    result = await p?.onBeforeSubmit?.(textInput, dlg);
                }
                if (result?.preventClose !== true) {
                    resolve(textInput.getValue() ?? "");
                }
                return result;
            },
            onDialogHide: (dlg) => {
                options?.dialog?.onDialogHide?.(dlg);
                // promise may have already been resolved in onConfirmButtonClick, it is ok to resolve again it will have no effect
                resolve(undefined);
            }
        });// no need to append the dialog to the DOM, it adds itself automatically

        dialog.setBody(textInput, options?.body);
    });
}